// src/components/signals/SignalCatalog.tsx
// Settings → Signals: read-only catalog of the deterministic signal types with
// utilization stats and a 30/90-day activity window toggle.
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { SignalCatalogCard } from "@/components/signals/SignalCatalogCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSignalCatalogStats } from "@/hooks/use-signal-catalog-stats";
import { SIGNAL_CATALOG } from "@/lib/signal-catalog";
import { SIGNAL_STATS_WINDOWS, type SignalStatsWindow } from "@/lib/signal-stats";
import { cn } from "@/lib/utils";

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border/60 bg-card px-4 py-3">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-2xl font-bold text-foreground">{value}</div>
    </div>
  );
}

export function SignalCatalog() {
  const [window, setWindow] = useState<SignalStatsWindow>(30);
  const { data, isLoading, isError, error, refetch } = useSignalCatalogStats(window);

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load signal statistics"}
        onRetry={() => refetch()}
      />
    );
  }

  const statsByType = new Map((data?.perType ?? []).map((t) => [t.type, t]));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          The signals the system generates from Solution data, the datasets each one uses, and how
          often they are raised and resolved. Signals are deterministic — computed from NOS Guidance
          and actual hours — and never change Intensity or Pod on their own; an RM confirms them on
          the triage queue.
        </p>
        <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
          {SIGNAL_STATS_WINDOWS.map((w) => (
            <Button
              key={w}
              type="button"
              variant="ghost"
              size="sm"
              aria-pressed={window === w}
              onClick={() => setWindow(w)}
              className={cn(
                "h-8 px-3 text-xs",
                window === w ? "bg-primary text-primary-foreground" : "text-muted-foreground",
              )}
            >
              {w}d
            </Button>
          ))}
        </div>
      </div>

      {isLoading || !data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            {["a", "b", "c"].map((k) => (
              <Skeleton key={k} className="h-20 w-full" />
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            {["x", "y", "z"].map((k) => (
              <Skeleton key={k} className="h-72 w-full" />
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <SummaryStat label="Open now (all types)" value={data.summary.totalOpen} />
            <SummaryStat label="Raised all-time" value={data.summary.totalRaised} />
            <SummaryStat
              label={`Resolved (last ${window}d)`}
              value={data.summary.totalResolvedInWindow}
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            {SIGNAL_CATALOG.map((entry) => (
              <SignalCatalogCard
                key={entry.type}
                entry={entry}
                stats={statsByType.get(entry.type)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
